/**
 *
 * ResetPassword
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import { compose } from "redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Alert, Button, Form, Input } from "antd";

// assets
import logoLogin from "images/logo-name.svg";
import history from "utils/history";
// utils
import api from "utils/api";

function ResetPassword({ flushState }) {
  const { t } = useTranslation();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [isForget, setIsForget] = useState(true);
  const [isActivation, setIsActivation] = useState(false);
  const [showSuccessForgot, setShowSuccessForgot] = useState(false);
  const [showErrorForgot, setShowErrorForgot] = useState(false);
  const [showSuccessReset, setShowSuccessReset] = useState(false);
  const [showErrorReset, setShowErrorReset] = useState(false);
  const urlParams = new URLSearchParams(location.search);

  const [resetForm] = Form.useForm();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here
    if (urlParams.get("id")) {
      setIsForget(false);
    }
    if (location?.pathname === "/activation/") {
      setIsActivation(true);
    }
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
    };
  }, []);

  const onFinishForgot = values => {
    setIsLoading(true);
    api.auth
      .forgotPassword(values)
      .then(result => {
        setShowSuccessForgot(true);
        setShowErrorForgot(false);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setShowSuccessForgot(false);
        setShowErrorForgot(true);
        setIsLoading(false);
      });
  };

  const onFinishFailedForgot = errorInfo => {
    console.error("Failed ResetPassword:", errorInfo);
  };

  const onFinishReset = values => {
    if (isActivation) {
      values.type = "activation";
    }
    // console.debug(values);
    setIsLoading(true);
    values.token = urlParams.get("id");
    api.auth.resetPassword(values)
      .then(result => {
        setShowSuccessReset(true);
        setShowErrorReset(false);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setShowSuccessReset(false);
        setShowErrorReset(true);
        setIsLoading(false);
      });
  };

  const onFinishFailedReset = errorInfo => {
    console.error("Failed ResetPassword:", errorInfo);
  };

  const onValuesChangeCallback = useCallback((changedValues, allValues) => {
    // Email has to be only lowercase on creation and without accents
    if (!!changedValues?.email) {
      let newEmail = changedValues?.email.toLowerCase();
      newEmail = newEmail.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      resetForm.setFieldsValue({
        email: newEmail,
      });
    }
  }, []);

  // render function
  return (
    <>
      <div className={"login-page"}>
        <div className="login-form__column">
          <div className="login-form__header">
            <img src={logoLogin} className="logo-login" alt="BeeMed Logo" />
            {isForget && showSuccessForgot === false && (
              <Form
                name="basic"
                className="login-form__container"
                initialValues={{ remember: true }}
                form={resetForm}
                onFinish={value => onFinishForgot(value)}
                onFinishFailed={errorInfo => onFinishFailedForgot(errorInfo)}
                onValuesChange={onValuesChangeCallback}
              >
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("inputs.mandatoryEmail"),
                    },
                  ]}
                >
                  <Input size="large" placeholder={t("inputs.emailPlaceholder")} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="login-submit"
                    loading={isLoading}
                  >
                    {t("inputs.resetPassword")}
                  </Button>
                </Form.Item>
                <br />
                {showErrorForgot && (
                  <Alert
                    message={t("errors.connectionError")}
                    description={t("errors.loginDetailMsg")}
                    type="error"
                    closable
                  />
                )}
              </Form>
            )}
            {isForget && showSuccessForgot === true && (
              <div className="success-container">
                <Alert
                  message={t("notifications.mailSentSuccess")}
                  description={t("notifications.mailSentdMsg")}
                  type="success"
                  closable
                />
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  className="back-login"
                  onClick={() => {
                    history.push("/login");
                  }}
                >
                  {t("pages.backToLogin")}
                </Button>
              </div>
            )}
            {!isForget && showSuccessReset === false && (
              <Form
                name="basic"
                className="login-form__container"
                initialValues={{ remember: true }}
                onFinish={value => onFinishReset(value)}
                onFinishFailed={errorInfo => onFinishFailedReset(errorInfo)}
              >
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("inputs.enterPassword"),
                    },
                    {
                      min: 6,
                      message: t("inputs.passwordMinimum6Char"),
                    },
                    {
                      pattern: /^(?=.*[A-Za-z\u00C0-\u017F])(?=.*\d).{6,}$/,
                      message: t("inputs.atLeastOneNumberAndLetter"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password placeholder={t("inputs.passwordPlaceholder")} size={"large"} />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: t("inputs.enterPassword"),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(t("errors.similarPassword")),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder={t("inputs.passwordPlaceholder")} size={"large"} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="login-submit"
                    loading={isLoading}
                  >
                    {t("common.save")}
                  </Button>
                  <br />
                </Form.Item>
                {showErrorReset && (
                  <Alert
                    message={t("errors.connectionError")}
                    description={t("errors.linkNotValid")}
                    type="error"
                    closable
                  />
                )}
              </Form>
            )}
            {!isForget && showSuccessReset === true && (
              <>
                <Alert
                  message={t("notifications.changePasswordSuccess")}
                  description={t("notifications.changePasswordMsg")}
                  type="success"
                  closable
                />
                {isActivation && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="back-login"
                    onClick={() => {
                      history.push("/login");
                    }}
                  >
                    {t("pages.backToLogin")}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <svg
          className="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shapeRendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
            />
          </defs>
          <g className="parallax">
            <use href="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
            <use
              href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255,255,255,0.5)"
            />
            <use
              href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255,255,255,0.3)"
            />
            <use href="#gentle-wave" x="48" y="7" fill="#fff" />
          </g>
        </svg>
      </div>
    </>
  );
}

export default compose(memo)(ResetPassword);
